import { useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import Image from 'next/image';

// component
import { PrimaryButton } from '@acko-tech/building-blocks.ui.button';

// utils
import { MPosContext } from 'utils/context';
import { getImageBasePath } from 'utils/generics';
import { useFetchParams } from 'hooks/useFetchParams';
import { PageLoader } from 'utils/styles';

// styles
import * as Styles from './styles';

// constants
import { PAYMENT_METHODS } from './constants';
import { getPaymentMethod } from './apis';
import SnackBar from 'library/snackbar';
import PaymentMethodOptionCard from './PaymentMethodOptionCard';
import { getPaymentMethodConfig, handleCompletePayment } from './helpers';

function PaymentMode() {
    const router = useRouter();
    const { lob } = useFetchParams();
    const { planDetails, partnerId, permissions } = useContext(MPosContext);
    const [orderAmount, setOrderAmount] = useState(0);
    const [paymentMethods, setPaymentMethods] = useState<string[]>([]);
    const [isWalletEnabled, setIsWalletEnabled] = useState(false);
    const [selectedPaymentMethod, setSelectedPaymentMethod] =
        useState<string>('');
    const [isBtnLoading, setIsBtnLoading] = useState(false);
    const [isFetchingPaymentDetails, setIsFetchingPaymentDetails] =
        useState(true);
    const [snackbar, setSnackbar] = useState({
        type: '',
        message: '',
    });
    const formattedOrderAmount = orderAmount?.toLocaleString('en-IN');
    const orderEkey = router.query?.order_ekey || planDetails?.order_ekey;
    const paymentMethodConfig = getPaymentMethodConfig(formattedOrderAmount);

    const onCompletePayment = async () => {
        await handleCompletePayment({
            selectedPaymentMethod,
            orderEkey,
            partnerId,
            setIsBtnLoading,
            setSnackbar,
            redirectToLogin: () => router.push('/login'),
            redirectToThankYou: (paymentOrderEkey: string) => {
                window.location.href = `/${lob}/${partnerId}/thankyou?payment_order_ekey=${paymentOrderEkey}`;
            },
            redirectToGateway: (
                paymentGatewayUrl: string,
                paymentOrderEkey: string
            ) => {
                window.location.href = `${paymentGatewayUrl}?id=${paymentOrderEkey}&redirect_source=pos_dealer`;
            },
        });
    };

    useEffect(() => {
        const fetchPaymentDetails = async () => {
            if (!orderEkey || !partnerId) {
                setIsFetchingPaymentDetails(false);
                return;
            }

            try {
                const {
                    data: {
                        net_payable,
                        available_method,
                        has_sufficient_balance,
                    },
                    status,
                    statusCode,
                } = await getPaymentMethod(
                    partnerId,
                    orderEkey,
                    permissions.group
                );
                if (statusCode === 401) {
                    router.push('/login');
                    setSnackbar({
                        type: 'failure',
                        message: 'Token expired. Redirecting to login page.',
                    });
                    return;
                }

                if (status && statusCode === 200) {
                    setOrderAmount(net_payable);
                    const methods = available_method || [
                        PAYMENT_METHODS.PAYMENT_GATEWAY,
                    ];
                    setPaymentMethods(methods);
                    setIsWalletEnabled(has_sufficient_balance);
                    setSelectedPaymentMethod(
                        has_sufficient_balance
                            ? PAYMENT_METHODS.WALLET
                            : PAYMENT_METHODS.PAYMENT_GATEWAY
                    );
                }
            } catch (error) {
                setSnackbar({
                    type: 'failure',
                    message: 'Failed to fetch payment details',
                });
            } finally {
                setIsFetchingPaymentDetails(false);
            }
        };

        fetchPaymentDetails();
    }, [partnerId, orderEkey]);

    const onBack = () => {
        router.back();
    };

    const renderRedirectInfoCard = () => (
        <Styles.RedirectInfoCard>
            <Styles.OptionTopRow>
                <Styles.IconWrapper>
                    <Image
                        src={getImageBasePath('/icons/card.svg')}
                        alt=""
                        width={'24px'}
                        height={'24px'}
                    />
                </Styles.IconWrapper>
                <Styles.RedirectInfoTitle>
                    You will be redirected to payment gateway
                </Styles.RedirectInfoTitle>
            </Styles.OptionTopRow>
            <Styles.RedirectInfoSubText>
                Complete payment of ₹{formattedOrderAmount}.
            </Styles.RedirectInfoSubText>
        </Styles.RedirectInfoCard>
    );

    if (isFetchingPaymentDetails) {
        return <PageLoader src={getImageBasePath('/gif/pageLoader.gif')} />;
    }

    return (
        <Styles.Wrapper>
            <Styles.BackArrowIcon>
                <Image
                    width={'10px'}
                    height={'24px'}
                    alt={''}
                    src={getImageBasePath('/icons/backArrow.svg')}
                    onClick={onBack}
                />
            </Styles.BackArrowIcon>

            <Styles.Title>Payment Mode</Styles.Title>
            <Styles.SubTitle>
                Please select from the available payment methods. Click complete
                payment to complete your order.
            </Styles.SubTitle>

            <Styles.AmountCard>
                <Styles.AmountLabel>Order Amount</Styles.AmountLabel>
                <Styles.AmountValue>₹{formattedOrderAmount}</Styles.AmountValue>
            </Styles.AmountCard>

            {paymentMethods.map((method) => (
                <PaymentMethodOptionCard
                    key={method}
                    method={method}
                    config={paymentMethodConfig[method]}
                    selectedPaymentMethod={selectedPaymentMethod}
                    isWalletDisabled={!isWalletEnabled}
                    onSelectMethod={setSelectedPaymentMethod}
                />
            ))}

            {paymentMethods.includes(PAYMENT_METHODS.PAYMENT_GATEWAY) &&
                selectedPaymentMethod === PAYMENT_METHODS.PAYMENT_GATEWAY && (
                    <>{renderRedirectInfoCard()}</>
                )}

            <Styles.StickyFooter>
                <PrimaryButton
                    onClick={onCompletePayment}
                    disabled={
                        isFetchingPaymentDetails || selectedPaymentMethod === ''
                    }
                    loading={isBtnLoading}
                >
                    Complete Payment
                </PrimaryButton>
            </Styles.StickyFooter>
            <SnackBar
                {...snackbar}
                handleSnakbarComplete={() =>
                    setSnackbar({ type: '', message: '' })
                }
            />
        </Styles.Wrapper>
    );
}

export default PaymentMode;
